import { ResizeNotifier } from '@theme/utilities';

/**
 * Event class for minimum visible item updates in nw-menu-list.
 * @extends {Event}
 */
export class NwMenuListMinimumEvent extends Event {
  /**
   * @param {boolean} minimumReached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class NwMenuList extends HTMLElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  connectedCallback() {
    if (this.#initialized) return;

    this.#initialize();
    this.#initialized = true;

    requestAnimationFrame(() => {
      this.#reflowItems();
    });
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#initialized || oldValue === newValue) return;

    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  setOverflowOpen(isOpen) {
    const { overflow, moreButton } = this.#refs;
    const shouldOpen = Boolean(isOpen && this.#hasOverflow);

    overflow.hidden = !shouldOpen;
    this.toggleAttribute('overflow-open', shouldOpen);

    if (moreButton) {
      moreButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    }
  }

  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #initialize() {
    const list = this.querySelector(':scope > [part="list"]');
    const overflow = this.querySelector(':scope > [part="overflow"]');
    const overflowList = overflow?.querySelector('[part="overflow-list"]');
    const placeholder = list?.querySelector(':scope > [part="placeholder"]');
    const moreItem = list?.querySelector(':scope > [slot="more"]');
    const moreButton = moreItem?.querySelector('[ref="menuitem"], button');

    if (
      !(list instanceof HTMLUListElement) ||
      !(overflow instanceof HTMLElement) ||
      !(overflowList instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement) ||
      !(moreItem instanceof HTMLLIElement) ||
      !(moreButton instanceof HTMLElement)
    ) {
      throw new Error('Invalid element types in <nw-menu-list />');
    }

    this.#refs = {
      list,
      overflow,
      overflowList,
      placeholder,
      moreItem,
      moreButton,
    };

    if (!moreButton.hasAttribute('aria-controls') && overflow.id) {
      moreButton.setAttribute('aria-controls', overflow.id);
    }

    this.setOverflowOpen(false);
    this.#observeChanges();
  }

  #getMenuItems() {
    const { list } = this.#refs;

    return Array.from(list.children).filter(
      (element) => element instanceof HTMLLIElement && !element.matches('[slot="more"], [part="placeholder"]')
    );
  }

  #moveItemsToDefaultList() {
    const { list, overflowList, moreItem, placeholder } = this.#refs;
    const insertionPoint = moreItem ?? placeholder ?? null;

    for (const element of Array.from(overflowList.children)) {
      if (!(element instanceof HTMLLIElement)) continue;

      element.removeAttribute('slot');
      list.insertBefore(element, insertionPoint);
    }
  }

  #reset() {
    const { list, placeholder, moreItem } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultList();
    this.#hasOverflow = false;

    list.style.removeProperty('height');
    list.style.removeProperty('flex-wrap');
    list.style.removeProperty('counter-reset');
    this.style.setProperty('--overflow-count', '0');

    placeholder.style.removeProperty('width');
    placeholder.hidden = true;

    moreItem.hidden = true;
    moreItem.style.removeProperty('order');
    this.setOverflowOpen(false);

    this.#observeChanges();
  }

  /**
   * @param {Element[]} visibleElements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems === null) return;

    const minimumReached = visibleElements.length < this.minimumItems;

    if (minimumReached) {
      this.setAttribute('minimum-reached', '');
    } else {
      this.removeAttribute('minimum-reached');
    }

    this.dispatchEvent(new NwMenuListMinimumEvent(minimumReached));
  }

  #reflowItems = () => {
    const { list, overflowList, placeholder, moreItem } = this.#refs;

    if (this.getAttribute('disabled') === 'true') return;

    this.#unobserveChanges();
    this.setOverflowOpen(false);
    this.#moveItemsToDefaultList();

    const elements = this.#getMenuItems();

    if (!elements.length) {
      moreItem.hidden = true;
      placeholder.hidden = true;
      this.#hasOverflow = false;
      this.#observeChanges();
      return;
    }

    /** @type {Element[]} */
    let visibleElements = [];
    /** @type {HTMLLIElement[]} */
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;
    moreItem.hidden = false;
    moreItem.style.setProperty('order', '-1');

    const moreRect = moreItem.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        if (element instanceof HTMLLIElement) {
          hasOverflow = true;
          overflowingElements.push(element);
        }
      } else {
        visibleElements.push(element);
      }
    });

    moreItem.style.removeProperty('order');

    for (const element of overflowingElements) {
      element.setAttribute('slot', 'overflow');
      overflowList.appendChild(element);
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    } else {
      placeholder.style.removeProperty('width');
      placeholder.hidden = true;
    }

    moreItem.hidden = !hasOverflow;
    this.#hasOverflow = hasOverflow;
    this.setOverflowOpen(false);

    this.#updateMinimumReached(visibleElements);
    this.#observeChanges();
  };

  #observeChanges() {
    const { list, overflowList } = this.#refs;

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(list, { childList: true });
    this.#mutationObserver.observe(overflowList, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;

    requestAnimationFrame(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  #initialized = false;
  #hasOverflow = false;
  #scheduled = false;

  /**
   * @type {{
   *   list: HTMLUListElement;
   *   overflow: HTMLElement;
   *   overflowList: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   *   moreItem: HTMLLIElement;
   *   moreButton: HTMLElement;
   * }}
   */
  #refs;

  #resizeObserver = new ResizeNotifier(this.#handleChange);
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('nw-menu-list')) {
  customElements.define('nw-menu-list', NwMenuList);
}
